/**
 * AssessmentNotes.tsx — clinical notes a clinician keeps on an ASSESSMENT while
 * filling a form on a patient's behalf, BEFORE any order case exists
 * (2026-07-20). The pre-checkout counterpart to CaseNotes.tsx (per-OrderCase) —
 * same grammar, keyed by assessmentId: the shared rich-text NoteEditor for add
 * + author-only edit, a chronological (oldest→newest) list with author,
 * timestamp, "· edited" and an author-only Edit link, and the same
 * defence-in-depth `sanitizeNoteHtml` strip before any body is rendered as HTML
 * (the SERVER is the real sanitiser).
 *
 * The wire carries `authorEmail: null`, so the display name is resolved
 * best-effort via the identity clinician-record read; an unresolvable id falls
 * back to the raw author id. That SAME read now also carries the author's ROLE
 * onto the row (2026-08-27): the compose route ADMITTED ADMIN as well as
 * CLINICIAN, so a name alone left an admin's note and a prescribing
 * clinician's note indistinguishable in a record titled "Clinical notes". The
 * derivation, and what it deliberately does NOT claim, live in noteAuthor.tsx.
 *
 * That grant was WITHDRAWN on 2026-08-28 (CTO ruling) — the route is
 * @Roles('CLINICIAN') now and this panel hides the composer from admins. The
 * badge did NOT become redundant: every note written under the old rule is
 * still in the record, and those are precisely the ones whose authorship a
 * reader cannot safely assume. It labels history, which is why it was derived
 * from identity rather than stamped on new rows.
 *
 * Wave 4 §4 (notes-per-answer): a note may carry a `questionKey` anchor — this
 * GENERAL area still lists every note (unanchored ones exactly as before) and
 * marks an anchored one with its question key, so nothing written against a
 * specific answer ever disappears from the running clinical record.
 */
import { useCallback, useEffect, useState } from 'react';
import { addAssessmentNote, editAssessmentNote, getAssessmentNotes } from '../api/clinic';
import type { CaseNote } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { noteByline } from '../lib/noteByline';
import { sanitizeNoteHtml } from '../lib/sanitizeHtml';
import { NoteEditor } from '../admin/callbacks/NoteEditor';
import { actionLinkStyle } from '../admin/callbacks/shared';
import { SectionTitle } from '../staff/ui';
import { Card, ErrorState, Help, Spinner } from '../ui/kit';
import { NoteAuthorLine, useNoteAuthors, type NoteAuthor } from './noteAuthor';

type Loadable<T> = { state: 'loading' | 'ready' | 'error'; data: T | null };

export function AssessmentNotesSection({
  assessmentId,
  heading = 'Clinical notes',
  composerLabel = 'Add a clinical note',
  composerTextboxLabel = 'New clinical note',
}: {
  assessmentId: string;
  /**
   * Card title / list label override (2026-08-27 drawer restructure). The
   * order-backed Review tab now renders THIS thread beside the per-case
   * CaseNotesSection, whose title IS "Clinical notes" — two identically-named
   * sections on one screen would be ambiguous to a reader and to assistive
   * tech, so that mount names this one "Assessment notes". Every other mount
   * keeps the established default. The empty/loading/error copy derives from
   * the heading so the wording stays consistent with the name on screen.
   */
  heading?: string;
  /** Composer form label — overridden with the heading for the same reason. */
  composerLabel?: string;
  composerTextboxLabel?: string;
}) {
  const { me } = useAuth();
  // WHO MAY WRITE, mirrored from the server (2026-08-28). clinic-service's
  // assessment-notes.controller.ts is @Roles('CLINICIAN') on both the add and
  // the edit route, so an ADMIN's submit now 403s. R41 keeps the SERVER as the
  // control and nothing here weakens that — this only stops the UI OFFERING an
  // action that is guaranteed to fail. Note the edit gate is role AND
  // authorship: a note an admin wrote BEFORE the rule changed is still theirs
  // by `authorUserId`, so authorship alone would keep showing them an Edit
  // button the server refuses.
  const canWriteClinicalNotes = me?.role === 'CLINICIAN';

  const [notes, setNotes] = useState<Loadable<CaseNote[]>>({ state: 'loading', data: null });
  // Name AND the role the identity clinician record supports — the same hook
  // CaseNotes.tsx uses, so the two threads that now render SIDE BY SIDE on the
  // order-backed Review tab label an author identically. See noteAuthor.tsx.
  const { authors, resolveAuthors } = useNoteAuthors();

  const load = useCallback(() => {
    setNotes({ state: 'loading', data: null });
    getAssessmentNotes(assessmentId)
      .then((list) => {
        setNotes({ state: 'ready', data: list.items });
        resolveAuthors(list.items);
      })
      .catch(() => setNotes({ state: 'error', data: null }));
  }, [assessmentId, resolveAuthors]);

  useEffect(() => {
    load();
  }, [load]);

  // Lower-cased heading for the derived copy ("No assessment notes yet.").
  const headingLower = heading.toLowerCase();

  return (
    <Card>
      <SectionTitle>{heading}</SectionTitle>
      {notes.state === 'loading' ? (
        <Spinner label={`Loading ${headingLower}…`} />
      ) : notes.state === 'error' || !notes.data ? (
        <ErrorState message={`Sorry — we could not load the ${headingLower}.`} onRetry={load} />
      ) : (
        <>
          {notes.data.length === 0 ? (
            <Help style={{ marginBottom: 'var(--gps-s-3)' }}>No {headingLower} yet.</Help>
          ) : (
            <ul aria-label={heading} style={{ listStyle: 'none', margin: '0 0 var(--gps-s-3)', padding: 0, display: 'grid', gap: '10px' }}>
              {[...notes.data]
                .sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))
                .map((note) => (
                  <AssessmentNoteRow
                    key={note.noteId}
                    note={note}
                    author={authors[note.authorUserId]}
                    canEdit={canWriteClinicalNotes && note.authorUserId === me?.userId}
                    assessmentId={assessmentId}
                    onChanged={load}
                  />
                ))}
            </ul>
          )}
          {canWriteClinicalNotes ? (
            <NoteEditor
            formLabel={composerLabel}
            textboxLabel={composerTextboxLabel}
            submitLabel="Add note"
            onSubmit={async (html) => {
              await addAssessmentNote(assessmentId, html);
              load();
            }}
            />
          ) : (
            // Deliberately says where the admin CAN write rather than only
            // what they cannot: admins and clinicians share the activity log,
            // which is the channel this panel is not.
            <Help>
              Only clinicians can add or edit clinical notes. You have read access here — to share
              something with the clinical team, use the activity log.
            </Help>
          )}
        </>
      )}
    </Card>
  );
}

function AssessmentNoteRow({
  note,
  author,
  canEdit,
  assessmentId,
  onChanged,
}: {
  note: CaseNote;
  author?: NoteAuthor;
  canEdit: boolean;
  assessmentId: string;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <li>
        <NoteEditor
          formLabel="Edit clinical note"
          textboxLabel="Clinical note text"
          submitLabel="Save note"
          initialHtml={note.body}
          onSubmit={async (html) => {
            await editAssessmentNote(assessmentId, note.noteId, html);
            setEditing(false);
            onChanged();
          }}
          onCancel={() => setEditing(false)}
        />
      </li>
    );
  }
  return (
    <li style={{ border: '1px solid var(--gps-border)', borderRadius: 'var(--gps-r-md)', padding: '10px 12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
        <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--gps-muted)' }}>
          <NoteAuthorLine note={note} author={author} />
          {note.questionKey ? (
            // Wave 4: an anchored note keeps its question marker here too.
            <span style={{ fontWeight: 400, marginLeft: '6px' }}>· on answer {note.questionKey}</span>
          ) : null}
        </span>
        <span style={{ fontSize: '11px', color: 'var(--gps-muted)' }}>
          {noteByline(note)}
        </span>
      </div>
      {/* Defence in depth (the server is the real sanitiser) — strip to a safe
          tag allowlist before ever rendering the note body as HTML. */}
      <div style={{ fontSize: '14px', color: 'var(--gps-text)' }} dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(note.body) }} />
      {canEdit ? (
        <button type="button" onClick={() => setEditing(true)} style={{ ...actionLinkStyle, marginTop: '6px' }}>
          Edit
        </button>
      ) : null}
    </li>
  );
}
